import type { AssetDataVal } from "@/api/client";

/** Cell voltages beside pack temperatures, at the BMS live page's own 3fr/1fr.
 *
 *  That page already pairs these two charts this way, and the temperature bars
 *  there map height as (t + 20) / 85, which this reuses so a person reading
 *  both tools sees the same shape for the same pack.
 *
 *  EVERY ROW IS IN FLOW. The first version of this put the value label and the
 *  balancing badge at a fixed negative offset from the column, which placed
 *  them OUTSIDE the chart box: they overlapped the card header, and they sat
 *  at one height instead of tracking each bar's top. Badge, value, bar and
 *  index stack now, and the bar takes the slack, so a label always sits on its
 *  own bar and nothing escapes the card.
 *
 *  THE EXTREMES ARE THE TWO ORANGES, not red and green: a lowest and a highest
 *  cell are not a fault, they are the ends of a spread, and spending the
 *  status colours on them would put a warning on every healthy pack. Lowest is
 *  the light tint, highest the dark one.
 *
 *  WHAT IS NOT HERE: a bar per NTC. The BMS reports every sensor
 *  (`cell_temperatures` with `num_cell_temperatures`, CAN 0x109), but the
 *  cluster firmware reduces them to min / ambient / MOSFET before the record
 *  goes on the wire, so Orbit never sees the individual ones. A per-NTC chart
 *  needs a repeated field on the record: 69 is the next free number, and the
 *  parser needs either a packed-signed path or a biased unsigned value.
 */

type Vals = Record<string, AssetDataVal>;
const num = (v: Vals, k: string): number | null =>
  typeof v[k] === "number" ? (v[k] as number) : null;

/** One column: badge slot, value, bar track, index. The badge slot is always
 *  present so the tracks line up across both charts whether or not a cell is
 *  balancing. */
function Bar({ pct, label, foot, tone, badge, title }: {
  pct: number; label: string; foot: string;
  tone?: "lo" | "hi"; badge?: boolean; title: string;
}) {
  return (
    <div className={`lw-bar${tone ? ` ${tone}` : ""}`} title={title}>
      {badge ? <span className="lw-bal" title="Balancing">B</span> : <span className="lw-bal-sp" />}
      <span className="lw-bv">{label}</span>
      <span className="lw-btrack"><span className="lw-bfill" style={{ height: `${pct}%` }} /></span>
      <span className="lw-bn">{foot}</span>
    </div>
  );
}

export function CellCharts({ v, cells }: { v: Vals; cells: number[] | null }) {
  // THE WINDOW IS FIXED, not derived from the data. A min/max-relative scale
  // makes a 4 mV spread look as alarming as a 400 mV one, because the shortest
  // bar is always empty and the tallest always full.
  const LO = 3000, HI = 4200;
  const lo = cells?.length ? Math.min(...cells) : null;
  const hi = cells?.length ? Math.max(...cells) : null;
  const spread = num(v, "cell_delta_mv") ?? (lo != null && hi != null ? hi - lo : null);
  const weak = num(v, "cell_min_idx");

  // min / ambient / MOSFET, the three the record actually carries, in deci-C
  const temps: Array<[string, number | null, string]> = [
    ["Min", num(v, "batt_temp_min_decic"), "Lowest pack sensor"],
    ["Amb", num(v, "batt_ambient_temp_decic"), "Ambient"],
    ["FET", num(v, "batt_mosfet_temp_decic"), "MOSFET"],
  ];
  const shown = temps.filter(([, t]) => t != null) as Array<[string, number, string]>;
  const tLo = shown.length ? Math.min(...shown.map(([, t]) => t)) : null;
  const tHi = shown.length ? Math.max(...shown.map(([, t]) => t)) : null;

  return (
    <div className="lw-charts">
      <div className="lw-chart">
        <div className="lw-chead">
          <span className="lw-ct">Cell voltages</span>
          <span className="lw-cd">
            {cells?.length ? `${cells.length} cells` : "not reported"}
            {spread != null ? ` · spread ${spread} mV` : ""}
          </span>
        </div>
        {cells?.length ? (
          <>
            <div className="lw-bars">
              {cells.map((mv, i) => (
                <Bar key={i}
                  pct={Math.max(8, Math.min(100, ((mv - LO) / (HI - LO)) * 100))}
                  label={String(mv)} foot={String(i + 1)}
                  tone={mv === lo ? "lo" : mv === hi ? "hi" : undefined}
                  // `cell_min_idx` is the BMS's own index for the weak cell and
                  // is 0-based on the wire; the labels here are 1-based.
                  badge={weak != null && weak === i}
                  title={`Cell ${i + 1}: ${mv} mV${mv === lo ? " (lowest)" : mv === hi ? " (highest)" : ""}`} />
              ))}
            </div>
            <div className="lw-legend">
              <span><i className="lw-sw mid" />In range</span>
              <span><i className="lw-sw lo" />Lowest</span>
              <span><i className="lw-sw hi" />Highest</span>
              {weak != null && <span><i className="lw-sw bal" />Weak cell #{weak + 1}</span>}
            </div>
          </>
        ) : (
          <div className="cap" style={{ padding: "18px 0 6px" }}>
            This device has not reported individual cell voltages. The snapshot
            keeps only the spread, which is beside the heading.
          </div>
        )}
      </div>

      <div className="lw-chart">
        <div className="lw-chead">
          <span className="lw-ct">Temperature</span>
          <span className="lw-cd">
            {tLo != null && tHi != null ? `Δ ${((tHi - tLo) / 10).toFixed(1)} °C` : "—"}
          </span>
        </div>
        {shown.length ? (
          <>
            <div className="lw-bars">
              {shown.map(([k, t, hint]) => (
                <Bar key={k}
                  // the BMS live page's own mapping, so both tools agree
                  pct={Math.max(6, Math.min(100, ((t / 10 + 20) / 85) * 100))}
                  label={(t / 10).toFixed(1)} foot={k}
                  title={`${hint}: ${(t / 10).toFixed(1)} °C`} />
              ))}
            </div>
            <div className="lw-legend"><span>Pack aggregates, not per sensor</span></div>
          </>
        ) : (
          <div className="cap" style={{ padding: "18px 0 6px" }}>
            No pack temperatures in this record.
          </div>
        )}
      </div>
    </div>
  );
}
